import { lazy, ReactNode, Suspense, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom';
import { retryImport } from '@shared/utils/utils';
import { useSelector } from 'react-redux';
import { INITIAL_ROUTE_PERMISSION_MAP } from './routePermissions';
import { selectCurrentUserPermissions } from '@features/auth/store/selectors';
import ProtectedRoute from './ProtectedRoute';
import LoadingPageSpinner from '@shared/components/others/LoadingPageSpinner/LoadingPageSpinner';
import {
    BOOKING_FAVORITES_ROUTE,
    BOOKING_MY_ROUTE,
    BOOKING_ROUTE,
    DEFECTS_ROUTE,
    EXECUTION_DASHBOARD_ROUTE,
    LIBRARY_ROUTE,
    MODULES_ROUTE,
    PROFILE_ROUTE,
    SCHEME_EDIT_ROUTE,
    SCHEME_ROUTE,
    SCHEME_SCRATCH_ROUTE,
    SCHEMES_FAVORITES_ROUTE,
    SCHEMES_ROUTE,
    STANDS_ROUTE,
    TESTCASE_HISTORY_ROUTE,
    TESTCASE_ROUTE,
    TESTCASES_ROUTE,
    TESTPLAN_HISTORY_ROUTE,
    TESTPLAN_ROUTE,
    TESTPLANS_ROUTE,
    TESTRESULT_AUTO_ROUTE,
    TESTRESULT_HAND_ROUTE,
    TESTRUN_ROUTE,
    TESTRUNS_ROUTE,
} from '@shared/constants/routesConstants';
import {
    UI_DASHBOARDS_PATH,
    UI_DATASHEETS_CHARTS_PATH,
    UI_DATASHEETS_PATH,
    UI_DATASHEETS_TABLE_PATH,
    UI_EXECUTION_PATH,
    UI_PERFORMANCE_PATH,
} from '@shared/constants/pathsConstants';
import { Layout } from '@app/layout';
import ErrorBoundaryFallback from '@shared/components/others/ErrorBoundary/ErrorBoundaryFallback';
import NoAccess from '@shared/components/others/NoAccess/NoAccess';
import DefectsPage from '@pages/defects/pages/DefectsPage';

const Dashboards = lazy(() => retryImport(() => import('@pages/dashboards/pages/Dashboards')));
const Testcases = lazy(() => retryImport(() => import('@pages/testcases/pages/TestcasesPage')));
const TestcasePage = lazy(() => retryImport(() => import('@pages/testcases/pages/TestcasePage')));
const TestcaseRunHistoryPage = lazy(() => retryImport(() => import('@pages/testruns/pages/TestcaseRunHistoryPage')));
const Testplans = lazy(() => retryImport(() => import('@pages/testplans/pages/TestplansPage')));
const TestplanPage = lazy(() => retryImport(() => import('@pages/testplans/pages/TestplanPage')));
const TestplanHistoryPage = lazy(() => retryImport(() => import('@pages/testplans/components/TestplanHistoryPage')));
const Testruns = lazy(() => retryImport(() => import('@pages/testruns/pages/Testruns')));
const TestrunPage = lazy(() => retryImport(() => import('@pages/testruns/pages/TestrunPage')));
const TestResultPage = lazy(() => retryImport(() => import('@pages/testruns/pages/TestResultPage')));
const SchemesPage = lazy(() => retryImport(() => import('@pages/schemes/pages/SchemesPage')));
const SchemeEditPage = lazy(() => retryImport(() => import('@pages/schemes/pages/SchemeEditPage')));
const SchemeScratchPage = lazy(() => retryImport(() => import('@pages/schemes/pages/SchemeScratchPage')));
const Profile = lazy(() => retryImport(() => import('@pages/profile/pages/Profile')));
const LibraryPage = lazy(() => retryImport(() => import('@pages/library/pages/LibraryPage')));
const Modules = lazy(() => retryImport(() => import('@pages/modules/pages/Modules')));
const BookingPage = lazy(() => retryImport(() => import('@pages/booking/pages/BookingPage')));
const StandsPage = lazy(() => retryImport(() => import('@pages/stands/pages/StandsPage')));

const NavigateToInitialPage = () => {
    const permissions = useSelector(selectCurrentUserPermissions);

    const initialRoute = useMemo(() => {
        const match = Object.entries(INITIAL_ROUTE_PERMISSION_MAP).find(([, permission]) =>
            permissions.includes(permission),
        );

        return match?.[0] ?? null;
    }, [permissions]);

    if (!initialRoute) return <NoAccess />;

    return <Navigate to={initialRoute} replace />;
};

const RouteElementContainer = ({ PageComponent }: { PageComponent: ReactNode }) => {
    return (
        <ProtectedRoute>
            <Suspense fallback={<LoadingPageSpinner />}>
                <Sentry.ErrorBoundary fallback={(errorData) => <ErrorBoundaryFallback errorData={errorData} />}>
                    {PageComponent}
                </Sentry.ErrorBoundary>
            </Suspense>
        </ProtectedRoute>
    );
};

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const AppRouter = sentryCreateBrowserRouter(
    createRoutesFromElements(
        <Route path="/" element={<Layout />}>
            <Route path="/" element={<NavigateToInitialPage />} />
            <Route path={UI_DASHBOARDS_PATH}>
                <Route index element={<Navigate to={EXECUTION_DASHBOARD_ROUTE} replace />} />
                <Route path={UI_PERFORMANCE_PATH} element={<RouteElementContainer PageComponent={<Dashboards />} />} />
                <Route path={UI_EXECUTION_PATH} element={<RouteElementContainer PageComponent={<Dashboards />} />} />
                <Route path={UI_DATASHEETS_PATH}>
                    <Route index element={<Navigate to={UI_DATASHEETS_TABLE_PATH} replace />} />
                    <Route
                        path={UI_DATASHEETS_TABLE_PATH}
                        element={<RouteElementContainer PageComponent={<Dashboards />} />}
                    />
                    <Route
                        path={UI_DATASHEETS_CHARTS_PATH}
                        element={<RouteElementContainer PageComponent={<Dashboards />} />}
                    />
                </Route>
            </Route>
            <Route path={MODULES_ROUTE} element={<RouteElementContainer PageComponent={<Modules />} />} />
            <Route path={TESTCASES_ROUTE} element={<RouteElementContainer PageComponent={<Testcases />} />} />
            <Route path={TESTCASE_ROUTE} element={<RouteElementContainer PageComponent={<TestcasePage />} />} />
            <Route
                path={TESTCASE_HISTORY_ROUTE}
                element={<RouteElementContainer PageComponent={<TestcaseRunHistoryPage />} />}
            />
            <Route path={TESTPLANS_ROUTE} element={<RouteElementContainer PageComponent={<Testplans />} />} />
            <Route path={TESTPLAN_ROUTE} element={<RouteElementContainer PageComponent={<TestplanPage />} />} />
            <Route
                path={TESTPLAN_HISTORY_ROUTE}
                element={<RouteElementContainer PageComponent={<TestplanHistoryPage />} />}
            />
            <Route path={TESTRUNS_ROUTE} element={<RouteElementContainer PageComponent={<Testruns />} />} />
            <Route path={TESTRUN_ROUTE} element={<RouteElementContainer PageComponent={<TestrunPage />} />} />
            <Route
                path={TESTRESULT_AUTO_ROUTE}
                element={<RouteElementContainer PageComponent={<TestResultPage />} />}
            />
            <Route
                path={TESTRESULT_HAND_ROUTE}
                element={<RouteElementContainer PageComponent={<TestResultPage isHandtest />} />}
            />
            <Route path={SCHEMES_ROUTE} element={<RouteElementContainer PageComponent={<SchemesPage />} />} />
            <Route path={SCHEMES_FAVORITES_ROUTE} element={<RouteElementContainer PageComponent={<SchemesPage />} />} />
            <Route path={SCHEME_ROUTE} element={<Navigate to="edit" replace />} />
            <Route path={SCHEME_EDIT_ROUTE} element={<RouteElementContainer PageComponent={<SchemeEditPage />} />} />
            <Route
                path={SCHEME_SCRATCH_ROUTE}
                element={<RouteElementContainer PageComponent={<SchemeScratchPage />} />}
            />
            <Route path={PROFILE_ROUTE} element={<RouteElementContainer PageComponent={<Profile />} />} />

            <Route path={LIBRARY_ROUTE} element={<RouteElementContainer PageComponent={<LibraryPage />} />} />
            <Route path={BOOKING_ROUTE} element={<RouteElementContainer PageComponent={<BookingPage />} />} />
            <Route path={BOOKING_FAVORITES_ROUTE} element={<RouteElementContainer PageComponent={<BookingPage />} />} />
            <Route path={BOOKING_MY_ROUTE} element={<RouteElementContainer PageComponent={<BookingPage />} />} />
            <Route path={STANDS_ROUTE} element={<RouteElementContainer PageComponent={<StandsPage />} />} />
            <Route path={DEFECTS_ROUTE} element={<RouteElementContainer PageComponent={<DefectsPage />} />} />
            {/*TODO uncomment when rule generator backend is ready*/}
            {/*<Route path={`/${UI_GENERATOR_PATH}/`} element={<Generator />} />*/}
            {/*<Route path={`/${UI_GENERATOR_PATH}/${UI_GENERATOR_PROFILE_PATH}/:profileId`} element={<ProfilePage />} />*/}
            {/*<Route path={`/${UI_GENERATOR_PATH}/${UI_RULESET_PATH}/:rulesetId`} element={<RuleSetPage />} />*/}
            <Route path={'*'} element={<div />} />
        </Route>,
    ),
);
